import { MagnifyingGlassIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { renderVerificationStatus } from "@utils/clinic";
import { formatAddress, toTitleCase } from "@utils/strings";
import { format } from "date-fns";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";

import { Button, type Column, Paragraph, Table } from "@fronterahealth/frontera-ui-components";

import {
  type GetClinicListingsOutput,
  VerificationStatusEnum,
  useBasecampClinicListingsRetrieve,
} from "@api/rest/types-and-hooks";

interface ClinicsTabProps {
  orgId: string;
}

export const ClinicsTab: React.FC<ClinicsTabProps> = ({ orgId }) => {
  const navigate = useNavigate();

  const CLINIC_COLUMNS: Column<GetClinicListingsOutput>[] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (value) => (
        <Paragraph displayType="loud" size="small" colorType="primary">
          {(value as string) ?? "—"}
        </Paragraph>
      ),
    },
    {
      title: "Address",
      dataIndex: "location",
      key: "location",
      render: (_value, record) => (
        <Paragraph size="small">
          {record.location
            ? formatAddress({
                addressLine1: toTitleCase(record.location.address_line_1) || undefined,
                addressLine2: toTitleCase(record.location.address_line_2 ?? "") || undefined,
                city: toTitleCase(record.location.city),
                state: record.location.state,
                zip: record.location.postal_code,
              }) || "—"
            : "—"}
        </Paragraph>
      ),
    },
    {
      title: "Status",
      dataIndex: "verification_status",
      key: "verification_status",
      render: (value) => renderVerificationStatus(value as VerificationStatusEnum),
    },
    {
      title: "Created",
      dataIndex: "created_date",
      key: "created_date",
      render: (value) => (
        <Paragraph size="small">{value ? format(new Date(value as string), "MM/dd/yyyy") : "—"}</Paragraph>
      ),
    },
  ];
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);

  const { data, isLoading } = useBasecampClinicListingsRetrieve(
    { org_id: orgId, search_term: debouncedSearchTerm || undefined },
    { query: { enabled: !!orgId } },
  );

  const clinicListings: GetClinicListingsOutput[] =
    data?.status === 200 ? (Array.isArray(data.data) ? data.data : [data.data]) : [];

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[640px] px-4 sm:px-8 lg:px-16 flex flex-col gap-6">
        <div className="flex items-center gap-4">
          <div className="flex flex-1 items-center gap-2 rounded-lg border border-border-strong bg-bg-surface px-3 py-2">
            <MagnifyingGlassIcon className="h-4 w-4 text-text-tertiary shrink-0" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search clinics..."
              className="w-full max-h-5 border-0 bg-transparent text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-0"
            />
          </div>
          <Button
            variant="primary"
            buttonStyle="filled"
            size="sm"
            text="+ Add Clinic"
            onClick={() => navigate(`/organizations/${orgId}/clinics/add`)}
          />
        </div>
        <Table<GetClinicListingsOutput>
          data={clinicListings}
          loading={isLoading}
          columns={CLINIC_COLUMNS}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          showPagination
          defaultItemsPerPage={10}
          itemsPerPageOptions={[10, 25, 50]}
          onRowClick={(row) => navigate(`/organizations/${orgId}/clinics/${row.id}`)}
          actions={[
            {
              label: "Edit",
              Icon: PencilSquareIcon,
              callback: (row) => navigate(`/organizations/${orgId}/clinics/${row.id}/edit`),
            },
          ]}
        />
      </div>
    </div>
  );
};
